import { Event, Events, Properties } from '../../enums/Analytics';
import Analytics, { EventProperty } from '../../tools/Analytics';

/**
 * Common properties shared by the DSR folder analytics events (DSR-4531).
 *
 * All fields are optional: a trigger site only sends the ones that are
 * reasonably available in its scope. Undefined values are stripped before the
 * event is dispatched so we never emit empty/`undefined` properties.
 *
 * Note: tenant, user role and stream are auto-attached globally by
 * `getBaseProperties` in `tools/Analytics.ts` and must not be re-added here.
 */
export interface FolderAnalyticsProps {
  roomId?: string | number;
  roomType?: string;
  folderId?: string | number;
  parentFolderId?: string | number | null;
  depth?: number;
}

function buildFolderEventProperties(
  props: FolderAnalyticsProps
): EventProperty {
  const eventProperties: EventProperty = {};

  if (props.roomId !== undefined && props.roomId !== null) {
    eventProperties[Properties.ROOM_ID] = props.roomId;
  }
  if (props.roomType !== undefined) {
    eventProperties[Properties.DSR_ROOM_TYPE] = props.roomType;
  }
  if (props.folderId !== undefined && props.folderId !== null) {
    eventProperties[Properties.FOLDER_ID] = props.folderId;
  }
  if (props.parentFolderId !== undefined && props.parentFolderId !== null) {
    eventProperties[Properties.PARENT_FOLDER_ID] = props.parentFolderId;
  }
  if (props.depth !== undefined) {
    eventProperties[Properties.FOLDER_DEPTH] = props.depth;
  }

  return eventProperties;
}

/**
 * Fires a DSR folder analytics event with the common folder properties.
 *
 * @warn Callers MUST gate this on the folder feature flag
 * (`OrganizationFlagEnum.RELEASE_CONTROL_DSR_FOLDERS`); no folder events should
 * fire when the flag is off.
 */
export function trackFolderEvent(
  event: Event,
  props: FolderAnalyticsProps
): void {
  Analytics.track(event, buildFolderEventProperties(props));
}

export const trackDsrFolderCreated = (props: FolderAnalyticsProps): void =>
  trackFolderEvent(Events.DSR_FOLDER_CREATED, props);

export const trackDsrFolderRenamed = (props: FolderAnalyticsProps): void =>
  trackFolderEvent(Events.DSR_FOLDER_RENAMED, props);

export const trackDsrFolderDeleted = (props: FolderAnalyticsProps): void =>
  trackFolderEvent(Events.DSR_FOLDER_DELETED, props);

export const trackDsrFolderOpenedRoomEditor = (
  props: FolderAnalyticsProps
): void => trackFolderEvent(Events.DSR_FOLDER_OPENED_ROOM_EDITOR, props);

export const trackDsrFolderOpenedRoomVisitor = (
  props: FolderAnalyticsProps
): void => trackFolderEvent(Events.DSR_FOLDER_OPENED_ROOM_VISITOR, props);

export const trackDsrFolderBreadcrumbClicked = (
  props: FolderAnalyticsProps
): void => trackFolderEvent(Events.DSR_FOLDER_BREADCRUMB_CLICKED, props);
